/**
 * CreateProject page - Form for creating a new project
 * Can be used as a modal or standalone page
 * Redirects to project settings after creation for brand context setup
 *
 * Phase 25: Added starter template selection step in the creation flow.
 * The selected starterKey is passed with the project creation request.
 */

import { ArrowLeft, Folder } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StarterSelector } from '@/components/template/StarterSelector';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks';
import { useCreateProject } from '@/hooks/useProjects';
import type { CreateProjectRequest } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

interface FormData {
  name: string;
  brandName: string;
  description: string;
  starterKey: string;
}

interface FormErrors {
  name?: string;
  brandName?: string;
  description?: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CreateProject() {
  const navigate = useNavigate();
  const { currentTeam } = useAuth();
  const createProjectMutation = useCreateProject();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    brandName: '',
    description: '',
    starterKey: 'standard',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Project name is required
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Project name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Project name must be less than 100 characters';
    }

    // Brand name is optional but has length limits
    if (formData.brandName.trim() && formData.brandName.trim().length > 100) {
      newErrors.brandName = 'Brand name must be less than 100 characters';
    }

    // Description is optional but has length limits
    if (formData.description.trim() && formData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleInputChange = (field: keyof Omit<FormData, 'starterKey'>) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleStarterSelect = (starterKey: string) => {
    setFormData((prev) => ({ ...prev, starterKey }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!currentTeam) {
      alert('No team selected. Please select a team first.');
      return;
    }

    // NOTE: The backend create_project endpoint may need to be updated to accept
    // starterKey. The frontend passes it so when the backend supports it, the
    // starter template will be automatically forked as the project's template.
    const projectData: CreateProjectRequest & { starterKey?: string } = {
      name: formData.name.trim(),
      brandName: formData.brandName.trim() || undefined,
      description: formData.description.trim() || undefined,
      starterKey: formData.starterKey,
    };

    try {
      const newProject = await createProjectMutation.mutateAsync({
        ...projectData,
        teamId: currentTeam.teamId,
      });

      // Navigate to project settings to set up brand context
      navigate(`/projects/${newProject.projectId}/settings?tab=brand-context`);
    } catch (error) {
      console.error('Failed to create project:', error);
      // Error is already handled by the mutation hook
      alert('Failed to create project. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-brand/10 text-brand flex-shrink-0">
            <Folder className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-primary leading-tight tracking-tight">
              Create New Project
            </h1>
            <p className="text-sm text-secondary mt-2">
              Set up a new project to organize and analyze your video creatives
            </p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Name */}
          <Input
            label="Project Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            placeholder="e.g., Summer Campaign 2024"
            error={!!errors.name}
            errorMessage={errors.name}
            helperText="A descriptive name for your project"
            required
            disabled={createProjectMutation.isPending}
            autoFocus
          />

          {/* Brand Name */}
          <Input
            label="Brand Name"
            value={formData.brandName}
            onChange={handleInputChange('brandName')}
            placeholder="e.g., Nike, Coca-Cola"
            error={!!errors.brandName}
            errorMessage={errors.brandName}
            helperText="The brand this project represents (optional)"
            disabled={createProjectMutation.isPending}
          />

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="mb-2 block text-sm font-medium text-secondary"
            >
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={handleInputChange('description')}
              placeholder="Add any additional details about this project..."
              rows={3}
              className="flex w-full font-sans text-md text-primary placeholder:text-muted transition-all duration-base ease-standard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 rounded-md border border-border-subtle bg-surface px-4 py-3 focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12 resize-none"
              disabled={createProjectMutation.isPending}
              aria-invalid={!!errors.description}
              aria-describedby={errors.description ? 'description-error' : 'description-helper'}
            />
            {errors.description && (
              <p id="description-error" className="mt-2 text-xs text-danger" role="alert">
                {errors.description}
              </p>
            )}
            {!errors.description && (
              <p id="description-helper" className="mt-2 text-xs text-muted">
                {formData.description.length}/500 characters
              </p>
            )}
          </div>

          {/* Starter Template Selection */}
          <div className="space-y-3 pt-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-secondary">
                Measurement Template
              </label>
              <p className="text-xs text-muted">
                Choose a starter template that defines how creatives will be evaluated.
                You can customize it later in project settings.
              </p>
            </div>
            <StarterSelector
              onSelect={handleStarterSelect}
              selectedKey={formData.starterKey}
              compact
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={createProjectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createProjectMutation.isPending}
              disabled={createProjectMutation.isPending}
            >
              Create Project
            </Button>
          </div>
        </form>
      </Card>

      {/* Info Card */}
      <Card className="bg-brand/5 border-brand/20">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-primary">
            What happens next?
          </h3>
          <p className="text-sm text-secondary">
            After creating your project, you&apos;ll be able to set up brand context
            including your logo, brand colors, distinctive assets, and product names.
            This helps our AI provide more accurate analysis of your creatives.
          </p>
        </div>
      </Card>
    </div>
  );
}
